import React, {useCallback, useMemo} from 'react'
import {Flex, SimpleGrid, Text, Container} from '@chakra-ui/react'
import {FormattedMessage, useIntl} from 'react-intl'
import {useCurrentCustomer} from '../../../hooks/use-current-customer'
import useMyStokkeRootPath from '../hooks/use-root-path'
import ContentSlot from '../../../components/content-slot'
import {CONTACT_US_HREF, FAQ_HREF, MY_STOKKE_PERSONAL_INFORMATION_PAGE_ID} from '../../../constants'
import {LogoutIcon, MessageQuestionMarkIcon, SendIcon} from '../../../components/icons'
import RecommendedProducts from '../../../components/recommended-products'
import useMultiSite from '../../../hooks/use-multi-site'
import useMyStokkeSubroutes from '../hooks/use-subroutes'
import TeaserLink from '../../../components/teaser-link'
import DashboardIconTile from '../../../components/dashboard-icon-tile'
import {trackEvent} from '../../../tracking/analytics'
import {
    ProductQuickviewModal,
    useProductQuickviewModal
} from '../../../components/product-quickview-modal'
import {AuthHelpers, useAuthHelper} from '@salesforce/commerce-sdk-react'
import useOcapiSite from '../../../hooks/useOcapiSite'
import MyStokkeSeo from '../partials/my-stokke-seo'
import ModuleContainer from '../../../leap/components/module-container'

const MyStokkeDashboard = () => {
    const {formatMessage} = useIntl()
    const {site} = useMultiSite()
    const {data: customer} = useCurrentCustomer()
    const subroutes = useMyStokkeSubroutes()
    const rootPath = useMyStokkeRootPath()
    const {isD2BCustomer} = customer
    const {
        preferences: {wishlistEnabled}
    } = useOcapiSite()

    const productQuickviewModalProps = useProductQuickviewModal()
    productQuickviewModalProps.onRecommendedProductClick = productQuickviewModalProps.onOpen

    const {list: subpagesList, map: subpagesMap} = useMemo(() => {
        const list = subroutes
            .filter((route) => {
                if (route.config?.id === 'wishlist') {
                    return wishlistEnabled && route.config
                } else if (route.config?.id === 'dashboard') {
                    return false
                } else {
                    return route.config
                }
            })
            .sort((a, b) => a.config.order - b.config.order)
        const map = Object.fromEntries(list.map((route) => [route.configId, route]))
        return {list: list.filter((item) => item.configId !== 'personal-information'), map}
    }, [subroutes, wishlistEnabled])

    const trackDashboardClick = (clickedID) => {
        trackEvent({
            event: 'dashboard_cta_click',
            page_type: 'My Stokke',
            click_text: clickedID
        })
    }

    const logout = useAuthHelper(AuthHelpers.Logout)
    const onLogoutClick = useCallback(async () => {
        try {
            await logout.mutateAsync()
            // my-stokke page will redirect to home page after logout
            trackDashboardClick('logout')
        } catch (error) {
            console.error('Error logging out', error)
        }
    }, [logout])

    if (!customer?.isRegistered) {
        return null
    }

    return (
        <>
            <Flex
                flexDirection="column"
                alignItems="center"
                data-testid="my-stokke-dashboard"
                bgColor="stokkeGray.bright"
            >
                <MyStokkeSeo />
                <Flex
                    flexDirection="column"
                    alignItems="center"
                    gap={2}
                    paddingTop={8}
                    paddingX={4}
                    paddingBottom={{base: 12, md: 14, lg: 16}}
                >
                    <Text as="h1" textStyle="Regular 45XLarge">
                        {isD2BCustomer ? (
                            <FormattedMessage
                                id="mystokke.dashboard.d2b.headline"
                                defaultMessage="Stokke Pro"
                            />
                        ) : (
                            <FormattedMessage
                                id="mystokke.dashboard.headline"
                                defaultMessage="My Stokke®"
                            />
                        )}
                    </Text>
                    <Text textStyle="Regular XLarge">
                        {isD2BCustomer && customer?.companyName ? (
                            customer.companyName
                        ) : (
                            <FormattedMessage
                                id="mystokke.dashboard.greeting"
                                defaultMessage="Hello, {firstName}! 👋"
                                values={{firstName: customer.firstName}}
                            />
                        )}
                    </Text>
                </Flex>
                <SimpleGrid
                    columns={{base: 2, md: Math.min(subpagesList.length, 3)}}
                    paddingX={{base: 4, md: 6, xl: 8}}
                    paddingBottom={{base: 4, md: 10}}
                    gap={{base: 4, md: 6, lg: 8}}
                >
                    {subpagesList.map((subpage, idx) => (
                        <DashboardIconTile
                            key={idx}
                            href={subpage.config.link}
                            isExternal={!!subpage.config.link}
                            to={subpage.path && rootPath + subpage.path}
                            icon={subpage.dashboardIcon}
                            label={subpage.config.headline}
                            onClick={() => trackDashboardClick(subpage.config.headline)}
                        />
                    ))}
                </SimpleGrid>

                <Container variant="no-padding">
                    <SimpleGrid
                        alignSelf="stretch"
                        columns={2}
                        padding={{base: 4, lg: 8}}
                        gap={{base: 4, lg: 8}}
                    >
                        <ContentSlot
                            slotId="account-promo"
                            sx={{gridColumn: '1 / span 2'}}
                            backgroundColor="stokkeCore.white"
                        />

                        {subpagesMap[MY_STOKKE_PERSONAL_INFORMATION_PAGE_ID] && (
                            <TeaserLink
                                sx={{gridColumn: '1 / span 2'}}
                                to={
                                    rootPath +
                                    subpagesMap[MY_STOKKE_PERSONAL_INFORMATION_PAGE_ID].path
                                }
                                title={
                                    subpagesMap[MY_STOKKE_PERSONAL_INFORMATION_PAGE_ID].config
                                        .headline
                                }
                                description={
                                    subpagesMap[MY_STOKKE_PERSONAL_INFORMATION_PAGE_ID].config
                                        .subline
                                }
                                onClick={() => trackDashboardClick('Personal Information')}
                            />
                        )}

                        <TeaserLink
                            to={FAQ_HREF}
                            icon={<MessageQuestionMarkIcon />}
                            title={<FormattedMessage id="global.link.faq" defaultMessage="FAQ" />}
                            onClick={() => trackDashboardClick('FAQ')}
                        />

                        <TeaserLink
                            to={CONTACT_US_HREF}
                            icon={<SendIcon />}
                            title={
                                <FormattedMessage
                                    id="global.link.contact_us"
                                    defaultMessage="Contact Us"
                                />
                            }
                            onClick={() => trackDashboardClick('ContactUs')}
                        />

                        <TeaserLink
                            sx={{gridColumn: '1 / span 2'}}
                            onClick={onLogoutClick}
                            icon={<LogoutIcon />}
                            title={
                                <FormattedMessage
                                    defaultMessage="Log Out"
                                    id="global.button.log_out"
                                />
                            }
                        />
                    </SimpleGrid>
                </Container>
            </Flex>

            <ModuleContainer background="subtle" spacing="recommender">
                <RecommendedProducts
                    recommender={`HP_recom_${site.id}`}
                    title={formatMessage({
                        defaultMessage: 'We think you’ll like these',
                        id: 'mystokke.dashboard.recommended_products.title'
                    })}
                    trackingLabel="My Stokke Recommendations"
                    onRecommendedProductClick={productQuickviewModalProps.onOpen}
                />
            </ModuleContainer>
            <ProductQuickviewModal {...productQuickviewModalProps} />
        </>
    )
}

export default MyStokkeDashboard
